
import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Trajetoria() {
  useEffect(() => {
    // Set page title and meta description
    document.title = 'Nossa Trajetória - 15 Anos de Experiência | 7deOuros';
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "Nossa Trajetória",
      "description": "15 anos entregando crescimento real. Da tecnologia corporativa para execução em PMEs.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://setedeouros.com.br'}/trajetoria`,
      "mainEntity": {
        "@type": "Organization",
        "name": "7deOuros",
        "foundingDate": "2009",
        "description": "Empresa especializada em sistemas de aumento de vendas"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const timeline = [
    {
      year: '2009-2015',
      title: 'Base em Multinacionais',
      description: 'Formação técnica robusta em líderes globais como Red Hat e Oracle, com certificações e atuação em projetos críticos que exigem escala, segurança e performance.'
    },
    {
      year: '2015-2018',
      title: 'Empreendedorismo e Performance',
      description: 'Desenvolvimento de soluções comportamentais para alta liderança, aplicadas em ambientes Microsoft, Oracle, Salesforce e SAS, gerando ganhos de desempenho mensuráveis.'
    },
    {
      year: '2018-2024',
      title: 'Método de Aumento de Vendas',
      description: 'Aplicação de método de aceleração comercial em mais de 300 pequenas empresas, alcançando elevação média de 30% no faturamento em 60 dias, com playbooks, métricas e rotinas de execução.'
    },
    {
      year: '2024-Presente',
      title: 'Sistema de Vendas para Médias',
      description: 'Sistema completo que integra processo comercial, site orientado a conversão e IA, combinando diagnóstico, automação e acompanhamento em tempo real para escalar vendas em empresas de médio porte.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(220, 38, 38, 0.9), rgba(153, 27, 27, 0.9)), url('https://readdy.ai/api/search-image?query=Professional%20business%20timeline%20showing%20growth%20and%20evolution%2C%20corporate%20success%20story%20with%20modern%20office%20buildings%20and%20technology%20elements%2C%20showing%20progression%20from%20startup%20to%20established%20company%2C%20clean%20minimalist%20design%20with%20red%20and%20orange%20accents%2C%20business%20people%20working%20on%20laptops%20and%20planning%20strategy&width=1920&height=1080&seq=trajetoria-7deouros&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              15 Anos Entregando Crescimento Real
            </h1>
            <p className="text-xl lg:text-2xl text-red-100 mb-8">
              Da tecnologia corporativa para execução em PMEs. Somos executores, não teóricos.
            </p>
          </div>
        </div>
      </section>
      
      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Nossa Jornada
            </h2>
          </div>
          
          <div className="relative">
            <div className="absolute left-1/2 transform -translate-x-px h-full w-0.5 bg-red-200"></div>
            
            {timeline.map((item, index) => (
              <div key={index} className={`relative flex items-center ${index % 2 === 0 ? 'justify-start' : 'justify-end'} mb-16`}>
                <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                  <div className="bg-white rounded-xl shadow-lg p-8 border-l-4 border-red-600">
                    <div className="flex items-center mb-4">
                      <div className="bg-red-600 text-white px-4 py-2 rounded-full font-bold text-lg">
                        {item.year}
                      </div>
                    </div>
                    <h3 className="text-xl font-semibold mb-3 text-gray-900">
                      {item.title}
                    </h3>
                    <p className="text-gray-600">
                      {item.description}
                    </p>
                  </div>
                </div>
                
                <div className="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 bg-red-600 rounded-full border-4 border-white shadow-lg"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Experience Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Nossa Experiência
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-gray-900">
                Tecnologia Corporativa
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Red Hat - Infraestrutura e Cloud Enterprise</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Oracle - Soluções de Grande Escala</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Projetos críticos com performance garantida</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-semibold mb-6 text-gray-900">
                Soluções Comportamentais
              </h3>
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Microsoft, Salesforce e SAS</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Ganhos de desempenho mensuráveis</span>
                </div>
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-check-line text-red-600"></i>
                  </div>
                  <span className="text-gray-700">Foco em alta liderança</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Why Choose Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Evolução Comprovada
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-building-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">Experiência Corporativa</h3>
              <p className="text-gray-600">
                Base sólida em multinacionais líderes globais com projetos de alta complexidade
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-rocket-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">Resultados</h3>
              <p className="text-gray-600">
                Sistema aplicado em 300+ empresas pequenas
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-line-chart-line text-2xl text-red-600"></i>
              </div>
              <h3 className="text-xl font-semibold mb-4">Resultados Comprovados</h3>
              <p className="text-gray-600">
                30% de aumento médio no faturamento em 60 dias com metodologia validada
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Numbers Section */}
      <section className="py-20 bg-red-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Números que Comprovam
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-orange-400 mb-2">15+</div>
              <p className="text-red-100">Anos de experiência</p>
            </div>
            
            <div>
              <div className="text-5xl font-bold text-orange-400 mb-2">300+</div>
              <p className="text-red-100">Implementações realizadas</p>
            </div>
            
            <div>
              <div className="text-5xl font-bold text-orange-400 mb-2">30%</div>
              <p className="text-red-100">Aumento médio em 60 dias</p>
            </div>
            
            <div>
              <div className="text-5xl font-bold text-orange-400 mb-2">60</div>
              <p className="text-red-100">Dias para resultado</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
            Experimente Nossa Metodologia
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Comece com uma análise gratuita do seu funil de vendas
          </p>
          <div className="flex justify-center">
            <a
              href="https://wa.me/5544988047699?text=Ol%C3%A1%21%20Quero%20fazer%20uma%20an%C3%A1lise%20gratuita%20do%20meu%20funil%20de%20vendas.%20Quando%20podemos%20conversar%3F"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center font-semibold whitespace-nowrap cursor-pointer transition-all duration-200 bg-red-600 hover:bg-red-700 text-white shadow-lg hover:shadow-xl px-8 py-4 text-lg rounded-xl"
            >
              Análise do Funil
            </a>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
